<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import { uploadImage } from "@/lib/uploads";
import { toDateTimeLocal } from "@/lib/date";
import { fetchRedemptionProduct, addRedemptionProduct, updateRedemptionProduct } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");
const form = ref({ name: "", points: "", quantity: "", startDate: "", endDate: "", imgUrl: "" });
const imageUploader = (file, p) => uploadImage("redemption_items", file, p);

onMounted(async () => {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const p = await fetchRedemptionProduct(id.value);
    if (!p) { notFound.value = true; return; }
    form.value = { name: p.name ?? "", points: p.points ?? "", quantity: p.quantity ?? "", startDate: toDateTimeLocal(p.startDate), endDate: toDateTimeLocal(p.endDate), imgUrl: p.imgUrl ?? "" };
  } catch { error.value = "Couldn't load the product."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const f = form.value;
  if (!f.name.trim()) { validationError.value = "Enter an item name."; return; }
  if (f.points === "") { validationError.value = "Enter the points cost."; return; }
  if (!f.imgUrl) { validationError.value = "Upload an item image."; return; }
  if (!f.startDate || !f.endDate) { validationError.value = "Start and end dates are required."; return; }
  const payload = { imgUrl: f.imgUrl, name: f.name.trim(), points: Number(f.points), quantity: f.quantity === "" ? null : Number(f.quantity), startDate: new Date(f.startDate), endDate: new Date(f.endDate) };
  saving.value = true;
  try {
    if (isEdit.value) await updateRedemptionProduct(id.value, payload); else await addRedemptionProduct(payload);
    success.value = true; setTimeout(() => router.push({ name: "redemption-products" }), 800);
  } catch { error.value = "Couldn't save. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'redemption-products' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Product" : "Add Product" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Product not found.</VAlert>
    <VCard v-else class="pa-4" max-width="640">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <VRow>
          <VCol cols="12"><VTextField v-model="form.name" label="Item Name" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.points" label="Points" type="number" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.quantity" label="Quantity (optional)" type="number" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.startDate" label="Start Date" type="datetime-local" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.endDate" label="End Date" type="datetime-local" /></VCol>
          <VCol cols="12"><UploadField v-model="form.imgUrl" kind="image" label="Item Image" :uploader="imageUploader" /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving">{{ isEdit ? "Update" : "Add Product" }}</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'redemption-products' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
